import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  MapPin,
  Mail,
  Clock,
  Ticket,
  Image as ImageIcon,
  Phone,
  Tag,
  Globe,
  CheckCircle,
  X,
  Plus,
} from "lucide-react";
import { useData } from '@happyspace/sdk/data';
import { usePreviewSafeData } from '../hooks/usePreviewSafeData';
import NewsTicker from './NewsTicker';
import { HERO_LOCATIONS_ALL, DFW_LOCATIONS_WITH_OTHER, filterLocationsByPrefix } from '../data/locations';
import { DEFAULT_CATEGORIES } from '../data/categories';
import { SHOW_GET_UPDATES_CARD } from '../config/siteFlags';

// Downscale + compress before turning into a data URL. The platform's API
// rejects large JSON bodies, so sending a raw multi-MB photo makes the whole
// submission fail to save. Matching the admin uploader (maxDim 1000, q0.82),
// which is proven to save on the platform, keeps the payload small.
function fileToDataUrl(file: File, maxDim = 1000, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Please choose an image file."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file isn't a valid image."));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(reader.result as string); // fallback: original data URL
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const out =
          file.type === "image/png" && scale === 1
            ? canvas.toDataURL("image/png")
            : canvas.toDataURL("image/jpeg", quality);
        resolve(out);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

const trendingItems = [
  "Restaurants",
  "Shopping",
  "Party Venues",
  "Grocery",
  "Concerts/Events",
  "Temples",
];

const trendingRoutes: Record<string, string> = {
  "Restaurants": "/restaurants",
  "Shopping": "/shopping",
  "Party Venues": "/party-venues",
  "Grocery": "/grocery",
  "Concerts/Events": "/concerts",
  "Temples": "/temples",
};

// Full canonical category list (kept in sync with data/categories.ts — the
// same list used for the homepage category buttons).
const EVENT_CATEGORIES = DEFAULT_CATEGORIES.map((c) => c.name);

const CUISINES = [
  { name: "North Indian", icon: "🍛" },
  { name: "South Indian", icon: "🥞" },
  { name: "Mediterranean", icon: "🐟" },
  { name: "Hookah Lounge", icon: "💨" },
  { name: "Bar & Grill", icon: "🍺" },
  { name: "Vegetarian", icon: "🌿" },
  { name: "Fusion", icon: "✨" },
  { name: "Pakistani", icon: "🥙" },
  { name: "Street Food", icon: "🌯" },
  { name: "Food Trucks", icon: "🚚" },
  { name: "Pizza", icon: "🍕" },
  { name: "Halal", icon: "🕌" },
  { name: "Others", icon: "📋" },
];

const inputClass =
  "w-full border rounded-xl px-3.5 py-2.5 text-[13px] text-gray-800 outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:border-orange-400 placeholder:text-gray-400";

interface FormState {
  eventName: string;
  eventCategory: string;
  location: string;
  fullAddress: string;
  isFree: string;
  ticketLink: string;
  contact: string;
  contactNumber: string;
  description: string;
  socialMediaLink: string;
  eventStartDateTime: string;
  eventEndDateTime: string;
}

const EMPTY_FORM: FormState = {
  eventName: "",
  eventCategory: "",
  location: "",
  fullAddress: "",
  isFree: "",
  ticketLink: "",
  contact: "",
  contactNumber: "",
  description: "",
  socialMediaLink: "",
  eventStartDateTime: "",
  eventEndDateTime: "",
};

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
      {children} {required && <span className="text-red-400">*</span>}
    </label>
  );
}

export default function AddEvent() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [email, setEmail] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("All Locations");
  const [showDropdown, setShowDropdown] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const locInputRef = useRef<HTMLInputElement>(null);
  const filteredLocations = filterLocationsByPrefix(HERO_LOCATIONS_ALL, locationSearch);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setLocationSearch("");
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  const { insert } = usePreviewSafeData("event_listings");
  const { insert: subscribeEmail } = useData("newsletter_subscribers");
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const [emailSubError, setEmailSubError] = useState<string | null>(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [showCuisineDropdown, setShowCuisineDropdown] = useState(false);
  const cuisineDropdownRef = useRef<HTMLDivElement>(null);
  const [showFormLocationDropdown, setShowFormLocationDropdown] = useState(false);
  const [formLocationSearch, setFormLocationSearch] = useState("");
  const formLocationDropdownRef = useRef<HTMLDivElement>(null);
  const formLocInputRef = useRef<HTMLInputElement>(null);
  const filteredFormLocations = filterLocationsByPrefix(DFW_LOCATIONS_WITH_OTHER, formLocationSearch);

  // Close cuisine dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (cuisineDropdownRef.current && !cuisineDropdownRef.current.contains(e.target as Node)) {
        setShowCuisineDropdown(false);
      }
    }
    if (showCuisineDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showCuisineDropdown]);

  // Close form location dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (formLocationDropdownRef.current && !formLocationDropdownRef.current.contains(e.target as Node)) {
        setShowFormLocationDropdown(false);
        setFormLocationSearch("");
      }
    }
    if (showFormLocationDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showFormLocationDropdown]);

  function update(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  }

  function toggleCuisine(name: string) {
    setSelectedCuisines((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  }

  function handleImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageName(file.name);
    setImagePreview(URL.createObjectURL(file));
    if (errors.media) setErrors((prev) => { const n = { ...prev }; delete n.media; return n; });
  }

  function removeImage() {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setImageName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!form.eventName.trim()) e.eventName = "Event name is required.";
    if (!form.eventCategory) e.eventCategory = "Please choose a category.";
    if (!form.location.trim()) e.location = "Location is required.";
    if (!form.fullAddress.trim()) e.fullAddress = "Full address is required.";
    if (!form.contact.trim()) e.contact = "Contact email is required.";
    if (!form.isFree) e.isFree = "Please select whether the event is free.";
    if (!imagePreview) e.media = "Please upload an event image.";
    if (form.ticketLink.trim() && !/^https?:\/\/.+/i.test(form.ticketLink.trim()))
      e.ticketLink = "Enter a valid URL (https://…).";
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitError(null);
    setSubmitting(true);
    try {
      const file = fileInputRef.current?.files?.[0];
      const imageUrl = file ? await fileToDataUrl(file) : null;
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      console.log('[AddEvent] inserting into event_listings...');
      await insert({
        eventName: form.eventName,
        eventCategory: form.eventCategory,
        // Raw datetime-local values are the single source of truth for
        // date/time — no derived duplicate columns.
        eventStartDateTime: form.eventStartDateTime,
        eventEndDateTime: form.eventEndDateTime,
        location: form.location,
        fullAddress: form.fullAddress,
        isFree: form.isFree,
        ticketLink: form.ticketLink,
        contact: form.contact,
        contactNumber: form.contactNumber,
        socialMediaLink: form.socialMediaLink,
        description: form.description,
        imageUrl,
        cuisines: selectedCuisines,
        source: "add-event-form",
        status: "New",
        submittedAt: today,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error('[AddEvent] insert failed:', err);
      setSubmitError(
        err instanceof Error ? err.message : "Couldn't reach the server. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    removeImage();
    setErrors({});
    setSubmitError(null);
    setSubmitted(false);
    setSelectedCuisines([]);
    setShowCuisineDropdown(false);
    setShowFormLocationDropdown(false);
    setFormLocationSearch("");
  }

  async function handleEmailSubscribe() {
    const trimmed = email.trim();
    if (!trimmed) { setEmailSubError("Please enter an email address."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setEmailSubError("Please enter a valid email."); return; }
    setEmailSubError(null);
    setEmailSubmitting(true);
    try {
      await subscribeEmail({ email: trimmed, source: "add-event-hero", subscribedAt: new Date().toISOString() });
      setEmailSubscribed(true);
      setEmail("");
    } catch (err) {
      setEmailSubError(err instanceof Error ? err.message : "Couldn't subscribe. Please try again.");
    } finally {
      setEmailSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* ─── Hero Section ─── */}
      <section
        className="relative w-full bg-cover bg-center"
        style={{
          backgroundImage:
            'url("https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/38c0a4b3b01c.jpeg")',
          minHeight: "160px",
        }}
      >
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative z-10 max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-3 sm:pt-4 pb-8 flex flex-col lg:flex-row items-start justify-between gap-4 lg:gap-6">
          <div className="flex-1 flex flex-col items-center text-center w-full min-w-0">
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-white text-[22px] sm:text-[25px] md:text-[27px] leading-tight tracking-tight drop-shadow-sm"
              style={{ fontFamily: "'Poppins', sans-serif", textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
            >
              <span className="font-normal">Connecting </span>
              <span className="font-extrabold">Desi's</span>
              <span className="font-normal"> in </span>
              <span className="font-extrabold">Dallas–DFW</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-1 text-white text-[13px] sm:text-[15px] md:text-[16px] font-semibold"
              style={{ fontFamily: "'Poppins', sans-serif", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
            >
              <span className="font-normal">Discover </span>
              <span className="text-[#fbbf24] font-bold">Businesses, Events, Reviews & Stories</span>
            </motion.p>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-3 w-full max-w-[740px]"
            >
              <div
                className="rounded-md"
                style={{
                  boxShadow: "0 0 40px 12px rgba(255,255,255,0.35), 0 0 80px 24px rgba(255,255,255,0.15)",
                  backdropFilter: "blur(4px)",
                }}
              >
                <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                  <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                    <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">
                      What are you looking for?
                    </span>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="e.g. Best Restaurants, Immigration Lawyer"
                      className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                    />
                  </div>

                  <div
                    ref={dropdownRef}
                    className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0"
                    style={{ zIndex: 99999 }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setShowDropdown(!showDropdown);
                        if (!showDropdown) setTimeout(() => locInputRef.current?.focus(), 0);
                      }}
                      className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]"
                    >
                      <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedLocation}</span>
                      <svg
                        className={"w-3 h-3 text-gray-400 shrink-0 transition-transform " + (showDropdown ? "rotate-180" : "")}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showDropdown && (
                      <div
                        className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg overflow-hidden flex flex-col"
                        style={{
                          zIndex: 99999,
                          marginTop: "4px",
                          width: "200px",
                          maxHeight: "260px",
                          boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        <div className="px-2 pt-2 pb-1.5 border-b border-gray-100 shrink-0">
                          <input
                            ref={locInputRef}
                            type="text"
                            value={locationSearch}
                            onChange={(e) => setLocationSearch(e.target.value)}
                            placeholder="Type a city..."
                            className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-gray-50 rounded-md px-2 py-1.5 border border-gray-200 focus:border-orange-300"
                          />
                        </div>
                        <div className="overflow-y-auto py-1">
                          {filteredLocations.length === 0 && (
                            <div className="px-3 py-2 text-[12px] text-gray-400">No cities found</div>
                          )}
                          {filteredLocations.map((loc) => (
                            <button
                              key={loc}
                              type="button"
                              onClick={() => { setSelectedLocation(loc); setShowDropdown(false); setLocationSearch(""); }}
                              className="w-full text-left flex items-center transition-colors duration-150"
                              style={{
                                padding: "8px 12px",
                                fontSize: "13px",
                                fontWeight: selectedLocation === loc ? 700 : 500,
                                color: selectedLocation === loc ? "#f97316" : "#111827",
                                background: selectedLocation === loc ? "#fff3e8" : "transparent",
                                height: "36px",
                              }}
                              onMouseEnter={(e) => { if (selectedLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }}
                              onMouseLeave={(e) => { if (selectedLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                            >
                              {loc}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto"
                  >
                    <span className="flex items-center justify-center gap-1.5">
                      <Search className="w-3.5 h-3.5 sm:hidden" />
                      Search
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>

          </div>

          {/* RIGHT — Get Updates card */}
          {SHOW_GET_UPDATES_CARD && (
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="hidden lg:flex flex-col bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 w-full lg:w-[260px] shrink-0 self-start mt-1"
          >
            <h3 className="text-[14px] font-bold text-gray-800 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Get Updates
            </h3>
            <p className="text-[11px] text-gray-500 mb-2">Events • Deals • New Businesses</p>
            {emailSubscribed ? (
              <div className="flex items-center gap-1.5 text-green-600 text-[12px] font-semibold py-1">
                <CheckCircle className="w-4 h-4" /> Subscribed!
              </div>
            ) : (
              <>
                <form
                  onSubmit={(e) => { e.preventDefault(); handleEmailSubscribe(); }}
                  className="flex items-center gap-2"
                >
                  <div className="flex-1 flex items-center bg-gray-50 rounded-md px-2.5 py-1.5 border border-gray-200 min-w-0">
                    <Mail className="w-3.5 h-3.5 text-gray-400 mr-1.5 shrink-0" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setEmailSubError(null); }}
                      placeholder="Your Email"
                      className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent min-w-0"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={emailSubmitting}
                    className="bg-[#51ba4f] hover:bg-[#43a041] disabled:opacity-60 text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 shrink-0"
                  >
                    {emailSubmitting ? "…" : "Subscribe"}
                  </button>
                </form>
                {emailSubError && <p className="text-red-400 text-[10.5px] mt-1">{emailSubError}</p>}
              </>
            )}
          </motion.div>
          )}
        </div>

        <svg
          viewBox="0 0 1440 24"
          preserveAspectRatio="none"
          style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block", zIndex: 3 }}
        >
          <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
        </svg>
      </section>
      <NewsTicker />

      {/* ─── Add Event Form ─── */}
      <section className="bg-gray-50/60 py-8 md:py-10" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[860px] w-[calc(100%-32px)] mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-[22px] md:text-[26px] font-extrabold text-gray-900">List Your Event</h2>
            <p className="text-[13px] text-gray-500 mt-1">
              Add your event to DallasDesiScoop and reach the DFW desi community.
            </p>
          </div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-green-100 p-8 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-[18px] font-bold text-gray-900">Event submitted!</h3>
              <p className="text-[13px] text-gray-500 mt-1.5 max-w-[440px] mx-auto">
                Thanks for submitting <span className="font-semibold text-gray-700">{form.eventName}</span>. Our team
                will review it and get back to you at <span className="font-semibold text-gray-700">{form.contact}</span>.
              </p>
              <button
                onClick={resetForm}
                className="mt-5 inline-flex items-center gap-1.5 bg-[#51ba4f] hover:bg-[#43a041] text-white text-[13px] font-semibold px-5 py-2.5 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" /> Add another event
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {/* Event Details */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6">
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-4">Event Details</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <FieldLabel required>Event Name</FieldLabel>
                    <input
                      type="text"
                      value={form.eventName}
                      onChange={(e) => update("eventName", e.target.value)}
                      placeholder="e.g. Diwali Mela & Food Festival"
                      className={`${inputClass} ${errors.eventName ? "border-red-400" : "border-gray-200"}`}
                    />
                    {errors.eventName && <p className="text-red-500 text-[11px] mt-1">{errors.eventName}</p>}
                  </div>

                  <div>
                    <FieldLabel required>Event Category</FieldLabel>
                    <div className="relative">
                      <Tag className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <select
                        value={form.eventCategory}
                        onChange={(e) => update("eventCategory", e.target.value)}
                        className={`${inputClass} pl-9 appearance-none bg-white ${errors.eventCategory ? "border-red-400" : "border-gray-200"} ${form.eventCategory ? "text-gray-800" : "text-gray-400"}`}
                      >
                        <option value="">Select a category</option>
                        {EVENT_CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <svg className="w-3 h-3 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {errors.eventCategory && <p className="text-red-500 text-[11px] mt-1">{errors.eventCategory}</p>}
                  </div>

                  {/* Types — shown only when "Restaurants" is selected */}
                  {form.eventCategory === "Restaurants" && (
                    <div ref={cuisineDropdownRef} className="relative">
                      <FieldLabel>Types</FieldLabel>
                      <button
                        type="button"
                        onClick={() => setShowCuisineDropdown(!showCuisineDropdown)}
                        className={`${inputClass} border-gray-200 text-left flex items-center justify-between`}
                      >
                        <span className={selectedCuisines.length > 0 ? "text-gray-800" : "text-gray-400"}>
                          {selectedCuisines.length > 0
                            ? selectedCuisines.join(", ")
                            : "Select cuisine types"}
                        </span>
                        <svg
                          className={"w-3 h-3 text-gray-400 shrink-0 transition-transform " + (showCuisineDropdown ? "rotate-180" : "")}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showCuisineDropdown && (
                        <div
                          className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto"
                          style={{
                            zIndex: 9999,
                            marginTop: "4px",
                            width: "100%",
                            maxHeight: "200px",
                            boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {CUISINES.map((cuisine) => {
                            const isSelected = selectedCuisines.includes(cuisine.name);
                            return (
                              <button
                                key={cuisine.name}
                                type="button"
                                onClick={() => toggleCuisine(cuisine.name)}
                                className="w-full text-left flex items-center gap-2 transition-colors duration-150"
                                style={{
                                  padding: "8px 12px",
                                  fontSize: "13px",
                                  fontWeight: isSelected ? 700 : 500,
                                  color: isSelected ? "#f97316" : "#111827",
                                  background: isSelected ? "#fff3e8" : "transparent",
                                  height: "36px",
                                }}
                                onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }}
                                onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                              >
                                <span>{cuisine.icon}</span>
                                <span>{cuisine.name}</span>
                                {isSelected && (
                                  <CheckCircle className="w-3.5 h-3.5 ml-auto text-orange-500" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* Show selected chips below */}
                      {selectedCuisines.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {selectedCuisines.map((c) => (
                            <span
                              key={c}
                              className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 text-[11px] font-medium px-2.5 py-1 rounded-full border border-orange-200"
                            >
                              {c}
                              <button
                                type="button"
                                onClick={() => toggleCuisine(c)}
                                className="hover:text-red-500 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <FieldLabel required>Location</FieldLabel>
                    <div className="relative" ref={formLocationDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowFormLocationDropdown(!showFormLocationDropdown)}
                        className={`${inputClass} pl-9 pr-10 text-left flex items-center gap-1.5 ${errors.location ? "border-red-400" : "border-gray-200"}`}
                      >
                        <MapPin className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <span className={form.location ? "text-gray-800" : "text-gray-400"}>
                          {form.location || "Select a location"}
                        </span>
                        <svg
                          className={"w-3 h-3 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 transition-transform " + (showFormLocationDropdown ? "rotate-180" : "")}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showFormLocationDropdown && (
                        <div
                          className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-lg py-1 overflow-hidden z-50"
                          style={{
                            marginTop: "4px",
                            boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
                          }}
                        >
                          <div className="px-3 py-2 border-b border-gray-100 sticky top-0 bg-white">
                            <input
                              ref={formLocInputRef}
                              type="text"
                              autoFocus
                              value={formLocationSearch}
                              onChange={(e) => setFormLocationSearch(e.target.value)}
                              placeholder="Type a city..."
                              className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                            />
                          </div>
                          <div className="overflow-y-auto" style={{ maxHeight: "200px" }}>
                            {filteredFormLocations.length > 0 ? (
                              filteredFormLocations.map((loc) => (
                                <button
                                  key={loc}
                                  type="button"
                                  onClick={() => { update("location", loc); setShowFormLocationDropdown(false); setFormLocationSearch(""); }}
                                  className="w-full text-left px-3 py-2 text-[13px] transition-colors duration-150 hover:bg-orange-50"
                                  style={{
                                    fontWeight: form.location === loc ? 700 : 500,
                                    color: form.location === loc ? "#f97316" : "#111827",
                                    background: form.location === loc ? "#fff3e8" : "transparent",
                                  }}
                                >
                                  {loc}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center text-[12px] text-gray-400">
                                No cities match "{formLocationSearch}"
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.location && <p className="text-red-500 text-[11px] mt-1">{errors.location}</p>}
                  </div>

                  {/* Full Address */}
                  <div>
                    <FieldLabel required>Full Address</FieldLabel>
                    <div className="relative">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        value={form.fullAddress}
                        onChange={(e) => update("fullAddress", e.target.value)}
                        placeholder="Street address, city, ZIP"
                        className={`${inputClass} pl-9 ${errors.fullAddress ? "border-red-400" : "border-gray-200"}`}
                      />
                    </div>
                    {errors.fullAddress && <p className="text-red-500 text-[11px] mt-1">{errors.fullAddress}</p>}
                  </div>



                  <div>
                    <FieldLabel>Event Start DateTime</FieldLabel>
                    <div className="relative">
                      <Clock className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="datetime-local"
                        value={form.eventStartDateTime}
                        onChange={(e) => update("eventStartDateTime", e.target.value)}
                        className={`${inputClass} pl-9 ${errors.eventStartDateTime ? "border-red-400" : "border-gray-200"}`}
                      />
                    </div>
                    {errors.eventStartDateTime && <p className="text-red-500 text-[11px] mt-1">{errors.eventStartDateTime}</p>}
                  </div>

                  <div>
                    <FieldLabel>Event End DateTime</FieldLabel>
                    <div className="relative">
                      <Clock className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="datetime-local"
                        value={form.eventEndDateTime}
                        onChange={(e) => update("eventEndDateTime", e.target.value)}
                        className={`${inputClass} pl-9 ${errors.eventEndDateTime ? "border-red-400" : "border-gray-200"}`}
                      />
                    </div>
                    {errors.eventEndDateTime && <p className="text-red-500 text-[11px] mt-1">{errors.eventEndDateTime}</p>}
                  </div>

                  <div>
                    <FieldLabel required>Free Event</FieldLabel>
                    <div className="relative">
                      <select
                        value={form.isFree}
                        onChange={(e) => update("isFree", e.target.value)}
                        className={`${inputClass} appearance-none bg-white ${errors.isFree ? "border-red-400" : "border-gray-200"} ${form.isFree ? "text-gray-800" : "text-gray-400"}`}
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                      <svg className="w-3 h-3 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {errors.isFree && <p className="text-red-500 text-[11px] mt-1">{errors.isFree}</p>}
                  </div>

                  <div className="sm:col-span-2">
                    <FieldLabel>Ticket Link</FieldLabel>
                    <div className="relative">
                      <Ticket className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="url"
                        value={form.ticketLink}
                        onChange={(e) => update("ticketLink", e.target.value)}
                        placeholder="https://tickets.example.com/your-event"
                        className={`${inputClass} pl-9 ${errors.ticketLink ? "border-red-400" : "border-gray-200"}`}
                      />
                    </div>
                    {errors.ticketLink && <p className="text-red-500 text-[11px] mt-1">{errors.ticketLink}</p>}
                  </div>
                </div>
              </div>

              {/* Media */}
              <div className={`bg-white rounded-2xl shadow-sm border p-5 md:p-6 ${errors.media ? "border-red-400" : "border-gray-100"}`}>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-4">
                  Media <span className="text-red-400">*</span>
                </h3>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImage} className="hidden" />
                {imagePreview ? (
                  <div className="flex items-center gap-4">
                    <img src={imagePreview} alt="Preview" className="w-28 h-20 object-cover rounded-lg border border-gray-200" />
                    <div className="min-w-0">
                      <p className="text-[12.5px] text-gray-700 font-medium truncate max-w-[260px]">{imageName}</p>
                      <button type="button" onClick={removeImage} className="mt-1 inline-flex items-center gap-1 text-[12px] text-red-500 hover:text-red-600 font-medium">
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-full border-2 border-dashed rounded-xl py-8 flex flex-col items-center justify-center transition-colors ${errors.media ? "border-red-300 hover:border-red-400 bg-red-50/30" : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/40"}`}
                  >
                    <ImageIcon className={`w-7 h-7 mb-2 ${errors.media ? "text-red-400" : "text-gray-400"}`} />
                    <span className="text-[13px] font-semibold text-gray-700">Attach an image</span>
                    <span className="text-[11px] text-gray-400 mt-0.5">PNG or JPG — your event flyer or photo</span>
                  </button>
                )}
                {errors.media && <p className="text-red-500 text-[11px] mt-2">{errors.media}</p>}
              </div>

              {/* Contact & Description */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6 space-y-4">
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide">Contact & Description</h3>
                <div>
                  <FieldLabel required>Contact Email</FieldLabel>
                  <div className="relative">
                    <Mail className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="email"
                      value={form.contact}
                      onChange={(e) => update("contact", e.target.value)}
                      placeholder="Email people can reach you at"
                      className={`${inputClass} pl-9 ${errors.contact ? "border-red-400" : "border-gray-200"}`}
                    />
                  </div>
                  {errors.contact && <p className="text-red-500 text-[11px] mt-1">{errors.contact}</p>}
                </div>
                <div>
                  <FieldLabel>Contact Number</FieldLabel>
                  <div className="relative">
                    <Phone className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="tel"
                      value={form.contactNumber}
                      onChange={(e) => update("contactNumber", e.target.value)}
                      placeholder="Phone number (optional)"
                      className={`${inputClass} pl-9 border-gray-200`}
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel>Description</FieldLabel>
                  <textarea
                    value={form.description}
                    onChange={(e) => update("description", e.target.value)}
                    rows={4}
                    placeholder="Tell the community about your event — what to expect, highlights, who it's for…"
                    className={`${inputClass} border-gray-200 resize-none`}
                  />
                </div>
              </div>

              {/* Social Media Link */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6">
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-4">Social Media</h3>
                <div>
                  <FieldLabel>Social Media Link</FieldLabel>
                  <div className="relative">
                    <Globe className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="url"
                      value={form.socialMediaLink}
                      onChange={(e) => update("socialMediaLink", e.target.value)}
                      placeholder="https://instagram.com/your-event or https://facebook.com/..."
                      className={`${inputClass} pl-9 border-gray-200`}
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-[12.5px] rounded-xl px-4 py-3">
                  {submitError}
                </div>
              )}
              <div className="flex justify-end">
                <motion.button
                  type="submit"
                  disabled={submitting}
                  whileHover={submitting ? undefined : { scale: 1.02 }}
                  whileTap={submitting ? undefined : { scale: 0.98 }}
                  className="bg-[#51ba4f] hover:bg-[#43a041] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-[14px] px-8 py-3 rounded-xl transition-colors"
                >
                  {submitting ? "Submitting…" : "Submit Event"}
                </motion.button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
